import {
  TriggersAndConditions,
  VideoTutorialCondition,
  VideoTutorialTrigger,
  videoTutorialConfig,
} from '@constants/video-tutorial.config';
import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {UtilsPL2 as U} from '@common/utils/dist/utils/utils-pl2.js';
import {Observable, OperatorFunction, combineLatest, merge} from 'rxjs';
import {concatMap, map, mapTo} from 'rxjs/operators';

export namespace VideoTutorialUtils {
  export type AnyTrigger = VideoTutorialTrigger[keyof VideoTutorialTrigger];
  export type TriggerName = keyof VideoTutorialTrigger;
  export type Trigger =
    | {name: 'route'; value: VideoTutorialTrigger['route']}
    | {name: 'user'; value: VideoTutorialTrigger['user']}
    | {name: 'device'; value: VideoTutorialTrigger['device']}
    | {name: 'resetAll'; value: undefined};

  function stateMatches<
    T extends VideoTutorialCondition[keyof VideoTutorialCondition],
  >(trigger: T, state: T, ifEmpty?: boolean): boolean;
  function stateMatches<
    T extends VideoTutorialTrigger[keyof VideoTutorialTrigger],
  >(trigger: T, state: T, ifEmpty?: boolean): boolean;
  function stateMatches<
    T extends
      | VideoTutorialTrigger[keyof VideoTutorialTrigger]
      | VideoTutorialCondition[keyof VideoTutorialCondition],
  >(trigger: T, state: T, ifEmpty = true): boolean {
    if (!trigger) {
      return ifEmpty;
    }

    return U.isFirstSubsetOfSecond(trigger, state);
  }

  function conditionsMatchesState(
    trigger: VideoTutorialCondition,
    states: VideoTutorialCondition,
  ): boolean {
    return (
      stateMatches(trigger.route, states.route) &&
      stateMatches(trigger.user, states.user) &&
      stateMatches(trigger.device, states.device) &&
      stateMatches(trigger.feature, states.feature)
    );
  }

  function triggerMatchesState(
    tutorialTrigger: VideoTutorialTrigger,
    trigger: Trigger,
  ): boolean {
    if (!trigger.value) {
      return false;
    }
    switch (trigger.name) {
      case 'route':
        return stateMatches(tutorialTrigger.route, trigger.value, false);
      case 'user':
        return stateMatches(tutorialTrigger.user, trigger.value, false);
      case 'device':
        return stateMatches(tutorialTrigger.device, trigger.value, false);
      default:
        console.warn('Unknown trigger name');
        return false;
    }
  }

  function matches(
    tutorialId: string,
    tutorialConditions: TriggersAndConditions | undefined,
    trigger: Trigger,
    states: VideoTutorialCondition,
    user: PL2.User,
  ): boolean {
    if (!tutorialConditions) {
      return false;
    }

    let triggerMatches = false;
    if (trigger.name === 'resetAll') {
      triggerMatches = tutorialConditions.triggers.some((tutorialTrigger) => {
        const mappedTriggers: VideoTutorialCondition = Object.entries(
          tutorialTrigger,
        ).reduce(
          (acc, [key, value]) => Object.assign(acc, {[key]: value.state}),
          {},
        );
        return conditionsMatchesState(mappedTriggers, states);
      });
    } else {
      triggerMatches = tutorialConditions.triggers.some((tutorialTrigger) =>
        triggerMatchesState(tutorialTrigger, trigger),
      );
    }
    if (!triggerMatches) {
      return false;
    }

    const tutorialWasDismissed = !!(user.tutorial ?? {})[tutorialId];
    if (tutorialWasDismissed) {
      return false;
    }

    const hasConditions = !!tutorialConditions.conditions.length;
    const conditionsMets =
      !hasConditions ||
      tutorialConditions.conditions.some((condition) =>
        conditionsMatchesState(condition, states),
      );
    return triggerMatches && conditionsMets;
  }

  export function findCorrectTutorials(
    trigger: Trigger,
    states: VideoTutorialCondition,
  ) {
    const user = states.user ?? {};
    return videoTutorialConfig.filter((tutorial) =>
      matches(tutorial?.id, tutorial.activation, trigger, states, user),
    );
  }

  export function shouldDeactivate(
    tutorialId: string,
    trigger: Trigger,
    states: VideoTutorialCondition,
  ) {
    if (!tutorialId) {
      return false;
    }

    const user = states.user ?? {};
    const tutorial = videoTutorialConfig.find((t) => t.id === tutorialId);
    if (!tutorial) {
      return false;
    }
    if (user.tutorial?.[tutorialId] === 1) {
      return true;
    }
    return matches(tutorial.id, tutorial.deactivation, trigger, states, user);
  }

  export function namedValue<T>(
    name: string,
  ): OperatorFunction<T, {name: string; value: T}> {
    return map((value) => ({name, value}));
  }

  export function distinctCombineLatest<
    R extends Record<string, Observable<T>>,
    T,
  >(observables: R): Observable<{last: keyof R; values: Record<keyof R, T>}> {
    const orderedKeys = Object.keys(observables);
    const orderedObservables = orderedKeys.map((key) => observables[key]);
    return new Observable<{last: keyof R; values: Record<keyof R, T>}>(
      (observer) => {
        const lastValues: Record<keyof R, T> = {} as Record<keyof R, T>;
        const distinctObservables = Object.entries(orderedObservables).map(
          ([_, value], index) => value.pipe(map((v) => ({index, value: v}))),
        );

        const combinedListener = combineLatest(distinctObservables);
        const combinedSubscription = combinedListener.subscribe(
          (values) => {
            values.forEach(({index, value}) => {
              lastValues[orderedKeys[index] as keyof R] = value;
            });
          },
          (err) => observer.error(err),
          () => {
            console.log('Combined listener completed');
            observer.complete();
            combinedSubscription.unsubscribe();
          },
        );

        const subscription = merge(...distinctObservables)
          .pipe(
            concatMap(({index, value}) =>
              combinedListener.pipe(mapTo({index, value})),
            ),
          )
          .subscribe(
            ({index}) => {
              observer.next({
                last: orderedKeys[index] as keyof R,
                values: lastValues,
              });
            },
            (err) => {
              observer.error(err);
            },
            () => {
              observer.next({
                last: orderedKeys[orderedKeys.length - 1] as keyof R,
                values: lastValues,
              });
              observer.complete();
              subscription.unsubscribe();
              console.log('Subscription completed');
            },
          );
      },
    );
  }
}
